import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { ReportPreview } from '@/components/patientReports/ReportPreview';
import { ReportSectionCards } from '@/components/patientReports/ReportSectionCards';
import { useAuth } from '@/lib/auth';
import { fetchFormsTemplateDetail, useFormsTemplateList } from '@/hooks/useFormsTemplates';
import {
  searchPreviewPatients,
  useDeletePatientReportTemplate,
  usePatientReportSources,
  usePatientReportTemplates,
  useSavePatientReportTemplate,
} from '@/hooks/usePatientReports';
import { revealReportCpr } from '@/lib/patientReports/cpr';
import { EMPTY_REPORT_CATALOG } from '@/lib/patientReports/model';
import {
  EXAMPLE_CPR,
  EXAMPLE_QUESTIONNAIRE_DETAIL,
  EXAMPLE_REPORT_CATALOG,
  createReportSection,
  questionsFromUnknown,
  resolveLetterheadMode,
  resolveReportPreview,
  templateSavePayload,
  type PatientReportTemplate,
  type ReportPreviewLabels,
  type ReportSection,
} from '@/lib/patientReports/template';
import type { PatientReportLetterheadChoice } from '@/lib/patientReports/model';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard } from '@/pages/settings/settingsChrome';

type Draft = {
  id: string | null;
  name: string;
  description: string;
  includeCover: boolean;
  coverTitle: string;
  coverBody: string;
  letterhead: PatientReportLetterheadChoice;
  sections: ReportSection[];
};

function emptyDraft(labels: { patient: string; markings: string; text: string }): Draft {
  return {
    id: null,
    name: '',
    description: '',
    includeCover: false,
    coverTitle: '',
    coverBody: '',
    letterhead: 'inherit',
    sections: [
      createReportSection('patient_fields', labels.patient),
      createReportSection('markings', labels.markings),
      createReportSection('text', labels.text),
    ],
  };
}

function draftFromTemplate(template: PatientReportTemplate): Draft {
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    includeCover: template.includeCover,
    coverTitle: template.coverTitle,
    coverBody: template.coverBody,
    letterhead: template.letterhead,
    sections: template.sections,
  };
}

export function PatientReportTemplateSettings({
  clinicId,
  canEdit,
  clinicLetterhead,
}: {
  clinicId: string | null;
  canEdit: boolean;
  clinicLetterhead: 'none' | 'invoice' | 'custom';
}) {
  const { t } = useTranslation();
  const list = usePatientReportTemplates(clinicId);
  const save = useSavePatientReportTemplate(clinicId);
  const remove = useDeletePatientReportTemplate(clinicId);
  const [draft, setDraft] = useState<Draft | null>(null);

  if (list.isLoading) {
    return <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.loading')}</p>;
  }
  if (list.error) {
    return <p className={cn(TYPE.body, 'text-ds-red-ink')}>{t('patientReports.load_error')}</p>;
  }

  return (
    <SectionCard
      title={t('patientReports.templates_title')}
      subtitle={t('patientReports.templates_hint')}
    >
      <p className={cn(TYPE.small, 'mb-s4 text-ds-muted')}>{t('patientReports.templates_v1')}</p>
      {draft ? (
        <TemplateDraftEditor
          clinicId={clinicId}
          canEdit={canEdit}
          draft={draft}
          tags={list.data?.tags ?? []}
          clinicLetterhead={clinicLetterhead}
          saving={save.isPending}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSave={async () => {
            if (!draft.name.trim()) {
              toast.error(t('patientReports.templates_name_required'));
              return;
            }
            const payload = templateSavePayload(draft);
            try {
              const saved = await save.mutateAsync({ templateId: draft.id, ...payload });
              setDraft(draftFromTemplate(saved));
              toast.success(t('patientReports.templates_saved'));
            } catch {
              toast.error(t('patientReports.templates_save_error'));
            }
          }}
        />
      ) : (
        <div className="flex flex-col gap-s3">
          {canEdit ? (
            <div>
              <Button
                onClick={() =>
                  setDraft(
                    emptyDraft({
                      patient: t('patientReports.add_patient'),
                      markings: t('patientReports.add_markings'),
                      text: t('patientReports.text_heading'),
                    }),
                  )
                }
              >
                {t('patientReports.templates_new')}
              </Button>
            </div>
          ) : null}
          {(list.data?.templates.length ?? 0) === 0 ? (
            <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.templates_empty')}</p>
          ) : (
            <ul className="divide-y divide-ds-line rounded-ctl border border-ds-line">
              {list.data?.templates.map((template) => (
                <li key={template.id} className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
                  <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.body, 'font-medium text-ds-text')}>{template.name}</p>
                    {template.description ? (
                      <p className={cn(TYPE.small, 'text-ds-muted')}>{template.description}</p>
                    ) : null}
                  </div>
                  <Button variant="secondary" onClick={() => setDraft(draftFromTemplate(template))}>
                    {t('patientReports.templates_edit')}
                  </Button>
                  {canEdit ? (
                    <Button
                      variant="tertiary"
                      loading={remove.isPending}
                      onClick={() => {
                        if (!window.confirm(t('patientReports.templates_delete_confirm'))) return;
                        void remove.mutateAsync(template.id).catch(() => {
                          toast.error(t('patientReports.templates_save_error'));
                        });
                      }}
                    >
                      {t('patientReports.templates_delete')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function TemplateDraftEditor({
  clinicId,
  canEdit,
  draft,
  tags,
  clinicLetterhead,
  saving,
  onChange,
  onClose,
  onSave,
}: {
  clinicId: string | null;
  canEdit: boolean;
  draft: Draft;
  tags: readonly { id: string; name: string }[];
  clinicLetterhead: 'none' | 'invoice' | 'custom';
  saving: boolean;
  onChange: (draft: Draft) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const forms = useFormsTemplateList(clinicId, i18n.language);
  const [previewMode, setPreviewMode] = useState<'example' | 'patient'>('example');
  const [patientQuery, setPatientQuery] = useState('');
  const [hits, setHits] = useState<{ id: string; name: string }[]>([]);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const sources = usePatientReportSources(
    clinicId,
    pickedId,
    previewMode === 'patient' && Boolean(pickedId),
  );
  const [cpr, setCpr] = useState<string | null>(null);
  const [cprAccess, setCprAccess] = useState<'hidden' | 'loading' | 'ready'>('hidden');
  const [formQuestions, setFormQuestions] = useState<
    Record<string, { id: string; text: string }[]>
  >({});

  const formOptions = useMemo(() => {
    const rows = [...(forms.data?.clinic ?? []), ...(forms.data?.platform ?? [])];
    return rows.map((row) => ({
      id: row.id,
      name: row.display_name_da || row.display_name_en || row.id,
    }));
  }, [forms.data]);

  const templateIds = draft.sections
    .flatMap((section) =>
      section.kind === 'questionnaire' && section.formsTemplateId ? [section.formsTemplateId] : [],
    )
    .join('|');

  useEffect(() => {
    if (!clinicId || !templateIds) return;
    let cancelled = false;
    void Promise.all(
      templateIds.split('|').map(async (templateId) => {
        try {
          const detail = await fetchFormsTemplateDetail(clinicId, templateId);
          return [templateId, questionsFromUnknown(detail.json_schema_preview)] as const;
        } catch {
          return [templateId, []] as const;
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, { id: string; text: string }[]> = {};
      for (const section of draft.sections) {
        if (section.kind !== 'questionnaire' || !section.formsTemplateId) continue;
        next[section.id] = [...(rows.find((row) => row[0] === section.formsTemplateId)?.[1] ?? [])];
      }
      setFormQuestions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [clinicId, draft.sections, templateIds]);

  useEffect(() => {
    if (previewMode !== 'patient' || !clinicId) return;
    const handle = window.setTimeout(() => {
      void searchPreviewPatients(clinicId, patientQuery)
        .then(setHits)
        .catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [clinicId, patientQuery, previewMode]);

  const needsCpr = draft.sections.some(
    (section) => section.kind === 'patient_fields' && section.fields.includes('cpr'),
  );
  const canReveal = Boolean(clinicId && hasPermission(clinicId, 'patients_cpr', 'view'));

  useEffect(() => {
    if (previewMode !== 'patient' || !pickedId || !needsCpr) {
      setCpr(null);
      setCprAccess(needsCpr && previewMode === 'patient' && !canReveal ? 'hidden' : 'ready');
      return;
    }
    if (!canReveal) {
      setCpr(null);
      setCprAccess('hidden');
      return;
    }
    let cancelled = false;
    setCprAccess('loading');
    void revealReportCpr(pickedId)
      .then((value) => {
        if (cancelled) return;
        setCpr(value);
        setCprAccess('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCpr(null);
        setCprAccess('ready');
      });
    return () => {
      cancelled = true;
      setCpr(null);
    };
  }, [canReveal, needsCpr, pickedId, previewMode]);

  const questions = { ...formQuestions };
  if (previewMode === 'example') {
    for (const section of draft.sections) {
      if (section.kind === 'questionnaire' && (questions[section.id]?.length ?? 0) === 0) {
        questions[section.id] = EXAMPLE_QUESTIONNAIRE_DETAIL.answers.map((answer) => ({
          id: answer.id,
          text: answer.prompt,
        }));
      }
    }
  }

  const catalog =
    previewMode === 'example' ? EXAMPLE_REPORT_CATALOG : (sources.data ?? EMPTY_REPORT_CATALOG);
  const labels = previewLabels(t);
  const model = resolveReportPreview({
    title: draft.name || t('patientReports.templates_name'),
    includeCover: draft.includeCover,
    coverTitle: draft.coverTitle,
    coverBody: draft.coverBody,
    letterhead: draft.letterhead,
    clinicLetterhead,
    sections: draft.sections,
    catalog,
    cprAccess: previewMode === 'example' ? 'ready' : cprAccess,
    cpr: previewMode === 'example' ? EXAMPLE_CPR : cpr,
    details:
      previewMode === 'example' ? { 'example-assignment': EXAMPLE_QUESTIONNAIRE_DETAIL } : {},
    fallbackDetail: previewMode === 'example' ? EXAMPLE_QUESTIONNAIRE_DETAIL : null,
    labels,
    showEmpty: true,
  });

  return (
    <div className="flex flex-col gap-s4">
      <div className="flex flex-wrap gap-s2">
        <Button variant="tertiary" onClick={onClose}>
          {t('patientReports.templates_back')}
        </Button>
        {canEdit ? (
          <Button loading={saving} onClick={onSave}>
            {t('patientReports.templates_save')}
          </Button>
        ) : null}
      </div>
      <div className="grid items-start gap-s4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-s3">
          <Input
            label={t('patientReports.templates_name')}
            value={draft.name}
            disabled={!canEdit}
            onChange={(event) => onChange({ ...draft, name: event.target.value })}
          />
          <Textarea
            label={t('patientReports.templates_description')}
            value={draft.description}
            disabled={!canEdit}
            onChange={(event) => onChange({ ...draft, description: event.target.value })}
          />
          <Select
            label={t('patientReports.letterhead_label')}
            value={draft.letterhead}
            disabled={!canEdit}
            onChange={(event) =>
              onChange({
                ...draft,
                letterhead: event.target.value as PatientReportLetterheadChoice,
              })
            }
          >
            <option value="inherit">{t('patientReports.letterhead_inherit')}</option>
            <option value="none">
              {t('settings.integrations.patient_reports.letterhead_none')}
            </option>
            <option value="invoice">
              {t('settings.integrations.patient_reports.letterhead_invoice')}
            </option>
            <option value="custom">
              {t('settings.integrations.patient_reports.letterhead_custom')}
            </option>
          </Select>
          <label className="flex items-center gap-s2">
            <input
              type="checkbox"
              checked={draft.includeCover}
              disabled={!canEdit}
              onChange={(event) => onChange({ ...draft, includeCover: event.target.checked })}
            />
            <span>{t('patientReports.cover')}</span>
          </label>
          {draft.includeCover ? (
            <>
              <Input
                label={t('patientReports.cover_title')}
                value={draft.coverTitle}
                disabled={!canEdit}
                onChange={(event) => onChange({ ...draft, coverTitle: event.target.value })}
              />
              <Textarea
                label={t('patientReports.cover_body')}
                value={draft.coverBody}
                disabled={!canEdit}
                onChange={(event) => onChange({ ...draft, coverBody: event.target.value })}
              />
            </>
          ) : null}
          <ReportSectionCards
            sections={draft.sections}
            tags={tags}
            questions={questions}
            formOptions={formOptions}
            disabled={!canEdit}
            onChange={(sections) => onChange({ ...draft, sections })}
          />
        </div>
        <div>
          <div className="mb-s3 flex flex-wrap gap-s2">
            <Button
              variant={previewMode === 'example' ? 'primary' : 'secondary'}
              onClick={() => setPreviewMode('example')}
            >
              {t('patientReports.preview_example')}
            </Button>
            <Button
              variant={previewMode === 'patient' ? 'primary' : 'secondary'}
              onClick={() => setPreviewMode('patient')}
            >
              {t('patientReports.preview_patient')}
            </Button>
          </div>
          {previewMode === 'patient' ? (
            <div className="mb-s3">
              <Input
                label={t('patientReports.preview_search')}
                value={patientQuery}
                onChange={(event) => setPatientQuery(event.target.value)}
              />
              <ul className="mt-s2">
                {hits.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      className={cn(TYPE.small, 'text-ds-text')}
                      onClick={() => setPickedId(hit.id)}
                    >
                      {hit.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <ReportPreview
            model={model}
            letterheadLabel={t(
              `patientReports.letterhead_${resolveLetterheadMode(draft.letterhead, clinicLetterhead)}`,
            )}
          />
        </div>
      </div>
    </div>
  );
}

function previewLabels(t: (key: string) => string): ReportPreviewLabels {
  return {
    name: t('patientReports.field_name'),
    cpr: t('patientReports.field_cpr'),
    email: t('patientReports.field_email'),
    phone: t('patientReports.field_phone'),
    address: t('patientReports.field_address'),
    jobTitle: t('patientReports.field_job'),
    cave: t('patientReports.field_cave'),
    notes: t('patientReports.field_notes'),
    diagnosis: t('patientReports.field_diagnosis'),
    patNumber: t('patientReports.field_pat_number'),
    locale: t('patientReports.field_locale'),
    notRecorded: t('patientReports.not_recorded'),
    cprHidden: t('patientReports.cpr_hidden'),
    cprLoading: t('patientReports.cpr_loading'),
    comment: t('patientReports.field_comment'),
    file: t('patientReports.kind_file'),
    page: t('patientReports.page'),
    quote: t('patientReports.field_quote'),
    score: t('patientReports.field_score'),
    answer: t('patientReports.field_answer'),
    missingQuestionnaire: t('patientReports.missing_questionnaire'),
  };
}
